import React, { useContext, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useQueryClient } from "react-query";

import Store from "../../../Store/Store";
import inventoryClient from "../../../clients/inventoryClient";
import SelectFamilyServingSizesModal from "../../../components/Modals/SelectFamilyServingSizesModal";
import PushFamilyServingSizesModal from "../../../components/Modals/PushFamilyServingSizesModal";

// pure.white inverts to black in dark mode in this theme, so use a fixed, slightly softened white
// for badge text that always sits on a blue background.
const BADGE_TEXT_WHITE = "rgba(255, 255, 255, 0.88)";

// The serving size affordance on a product family row - "+ Add Serving Size" when the family has
// none, otherwise the first preset plus a count of the rest. Opens the picker, and on save offers
// to push the presets down to formats already in the library.
const FamilyServingSizeControl = ({ productFamily, onSaved, onError }) => {
  const [pickerOpen, togglePickerOpen] = useState(false);
  const [pushModalOpen, togglePushModalOpen] = useState(false);

  const store = useContext(Store);
  const queryClient = useQueryClient();
  const establishmentId = store.currentEstablishment?.id;

  const servingSizes = productFamily?.defaultServingSizes ?? [];
  const hasSelection = servingSizes.length > 0;
  const remainingCount = hasSelection ? servingSizes.length - 1 : 0;

  const refreshFamilies = () => {
    queryClient.invalidateQueries({ queryKey: ["productFamilyGroups"] });
    queryClient.refetchQueries({ queryKey: ["productFamilyGroups"] });
  };

  const handleSave = async (servingSizeIds) => {
    try {
      await inventoryClient.setFamilyDefaultServingSizes({
        establishmentId,
        familyId: productFamily.id,
        servingSizeIds,
      });

      refreshFamilies();
      togglePushModalOpen(true);
    } catch {
      onError?.("There was an error saving the serving size presets");
    }
  };

  const handlePush = async () => {
    try {
      const result = await inventoryClient.pushFamilyServingSizesToFormats({
        establishmentId,
        familyId: productFamily.id,
      });

      const updated = result?.formatsUpdated ?? 0;
      onSaved?.(
        updated === 1
          ? "Serving sizes pushed to 1 product format"
          : `Serving sizes pushed to ${updated} product formats`
      );

      // formats changed, so the product library is stale now
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      queryClient.invalidateQueries({ queryKey: ["inventory-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["paginated-product-search"] });
    } catch {
      onError?.("There was an error pushing the serving sizes to products");
    }
  };

  return (
    <>
      <Box
        onClick={() => togglePickerOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          cursor: "pointer",
          height: "32px",
          minWidth: "180px",
          paddingX: 3,
          marginRight: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: (theme) => theme.palette.terrain[300],
          overflow: "hidden",
          "&:hover": {
            borderColor: (theme) => theme.palette.primary[800],
          },
        }}
      >
        {hasSelection ? (
          <>
            <Chip
              size="small"
              label={servingSizes[0].name}
              sx={{
                maxWidth: "110px",
                fontWeight: 600,
                borderRadius: "6px",
                backgroundColor: (theme) => theme.palette.info[600],
                color: BADGE_TEXT_WHITE,
                "& .MuiChip-label": { color: BADGE_TEXT_WHITE },
              }}
            />
            {remainingCount > 0 ? (
              <Chip
                size="small"
                label={`+${remainingCount}`}
                sx={{
                  fontWeight: 700,
                  borderRadius: "6px",
                  backgroundColor: (theme) => theme.palette.info[800],
                  color: BADGE_TEXT_WHITE,
                  "& .MuiChip-label": { color: BADGE_TEXT_WHITE },
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            <AddOutlinedIcon
              sx={{
                fontSize: 14,
                color: (theme) => theme.palette.primary[800],
              }}
            />
            <Typography
              variant="smallButton"
              noWrap
              sx={{ color: (theme) => theme.palette.primary[800] }}
            >
              Add Serving Size
            </Typography>
          </>
        )}
      </Box>

      <SelectFamilyServingSizesModal
        isOpen={pickerOpen}
        toggleModalOpen={togglePickerOpen}
        productFamily={productFamily}
        onSave={handleSave}
      />
      <PushFamilyServingSizesModal
        isOpen={pushModalOpen}
        toggleModalOpen={togglePushModalOpen}
        handlePush={handlePush}
      />
    </>
  );
};

export default FamilyServingSizeControl;
